import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatBalance } from '../lib/money';
import { Modal } from './Modal';

const HANDOFF_KEY = 'llmr-promo-credit-welcome';

/**
 * member-api hands this out exactly once, on whichever login (or 2FA
 * verify) first follows a signup promo code's redemption — see
 * issueSession's own comment. That response is read inside SignIn.tsx,
 * which unmounts a moment later when App.tsx flips to 'signed-in' and
 * mounts Layout fresh; sessionStorage is what survives that handoff, same
 * mechanism OrgChooserModal already uses for its own "seen" flag, used here
 * instead to carry the one-shot payload itself across the remount.
 *
 * Nothing re-arms this: the server never sends promo_credit a second time
 * for the same credit, so there is no "seen" flag to manage here at all —
 * presence in sessionStorage IS the trigger, and reading it also consumes
 * it (a stray reload of the dashboard must not show this a second time in
 * the same tab either).
 */
export function stagePromoCreditWelcome(credit: { amount: string; currency: string }) {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(credit));
  } catch {
    // Private-browsing / storage-blocked: the pop-up just won't show. The
    // credit itself already landed in the ledger regardless — see
    // llmr-control-api's RedeemPromoCode.
  }
}

export function PromoCreditWelcomeModal() {
  const { dict } = useLanguage();
  const [credit, setCredit] = useState<{ amount: string; currency: string } | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(HANDOFF_KEY);
      if (!raw) return;
      sessionStorage.removeItem(HANDOFF_KEY);
      setCredit(JSON.parse(raw));
    } catch {
      // Malformed/blocked storage: nothing to show, nothing to crash over.
    }
  }, []);

  return (
    <AnimatePresence>
      {credit && (
        <Modal title={dict.promoCreditWelcome.title} onClose={() => setCredit(null)}>
          <p className="note">{dict.promoCreditWelcome.body(formatBalance(credit.amount))}</p>
          <div className="modal-actions">
            <button type="button" onClick={() => setCredit(null)}>
              {dict.promoCreditWelcome.close}
            </button>
          </div>
        </Modal>
      )}
    </AnimatePresence>
  );
}
